/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// invite-flow Wave-3B — shared call-history list. Mirrors the web
// `CallHistoryHome` Tailwind layout verbatim, but rendered through
// React-Native primitives + NativeWind so both `apps/web` and
// `apps/desktop` consume the same UI.
//
// Data is pushed in via props; the parent host owns the loader, the
// "start call" action (which on web hits `POST /v1/calls/start` and
// then mounts the WebRTC overlay), and the "more" / search wiring.

import type React from "react";
import { useMemo, useState } from "react";
import {
	ArrowDownLeft,
	ArrowUpRight,
	MoreHorizontal,
	Phone,
	PhoneMissed,
	Plus,
	Search,
	Video,
} from "lucide-react-native";
import {
	Pressable,
	ScrollView,
	Text,
	TextInput,
	View,
} from "react-native";
import { Avatar } from "../../components/Avatar";
import type {
	CallHistoryEntry,
	CallKind,
	CallParticipant,
} from "../types";

const MIN = 60_000;
const HOUR = 60 * MIN;

export interface CallHistoryListProps {
	readonly calls: readonly CallHistoryEntry[];
	readonly isLoading: boolean;
	readonly onStartCall: (userId: string, kind: CallKind) => void;
	readonly onNewCall?: (() => void) | undefined;
	readonly onMorePress?: ((entry: CallHistoryEntry) => void) | undefined;
}

export const CallHistoryList: React.FC<CallHistoryListProps> = ({
	calls,
	isLoading,
	onStartCall,
	onNewCall,
	onMorePress,
}) => {
	const [query, setQuery] = useState("");

	const favourites = useMemo<readonly CallParticipant[]>(() => {
		const seen = new Set<string>();
		const out: CallParticipant[] = [];
		for (const c of calls) {
			if (seen.has(c.user.id)) continue;
			seen.add(c.user.id);
			out.push(c.user);
			if (out.length >= 6) break;
		}
		return out;
	}, [calls]);

	const filteredCalls = useMemo<readonly CallHistoryEntry[]>(() => {
		const q = query.trim().toLowerCase();
		if (q.length === 0) return calls;
		return calls.filter((c) =>
			c.user.displayName.toLowerCase().includes(q),
		);
	}, [calls, query]);

	return (
		<ScrollView
			testID="calls-panel"
			accessibilityLabel="Calls"
			className="flex-1 bg-light-surface-primary dark:bg-[#0A0B0F]"
		>
			<View className="flex flex-col gap-xs px-lg pt-lg pb-sm">
				<View className="flex flex-row items-center justify-between">
					<Text className="text-light-text-primary dark:text-white font-sans font-bold text-[22px] leading-[1.2]">
						Calls
					</Text>
					{onNewCall !== undefined && (
						<Pressable
							testID="calls-new-call"
							accessibilityRole="button"
							accessibilityLabel="New call"
							onPress={onNewCall}
							className="
								flex items-center justify-center w-9 h-9 rounded-lg
								active:opacity-70
							"
						>
							<Plus size={18} strokeWidth={1.75} color="#9aa0a6" />
						</Pressable>
					)}
				</View>
			</View>

			<View className="px-lg pb-md">
				<View
					testID="calls-search"
					className="
						flex flex-row items-center gap-sm
						px-md py-sm rounded-lg
						bg-light-surface-secondary dark:bg-white/[0.04]
						border border-light-border-secondary dark:border-white/[0.04]
					"
				>
					<Search size={16} strokeWidth={1.75} color="#9aa0a6" />
					<TextInput
						accessibilityLabel="Search calls"
						placeholder="Search by name or company…"
						placeholderTextColor="#9aa0a6"
						value={query}
						onChangeText={setQuery}
						className="
							flex-1 font-sans text-footnote
							text-light-text-primary dark:text-white
						"
					/>
				</View>
			</View>

			{favourites.length > 0 && (
				<View className="px-lg pb-lg">
					<Text className="font-sans text-[11px] font-semibold uppercase tracking-wider text-light-text-tertiary dark:text-white/40 mb-sm">
						Favourites
					</Text>
					<ScrollView
						horizontal
						showsHorizontalScrollIndicator={false}
						contentContainerClassName="flex flex-row gap-md pb-xs"
					>
						{favourites.map((user) => (
							<FavouriteCard
								key={user.id}
								user={user}
								onPress={() => onStartCall(user.id, "audio")}
							/>
						))}
					</ScrollView>
				</View>
			)}

			<View className="px-lg pb-xxl">
				<Text className="font-sans text-[11px] font-semibold uppercase tracking-wider text-light-text-tertiary dark:text-white/40 mb-sm">
					Recent
				</Text>
				{isLoading ? (
					<CallHistorySkeleton />
				) : filteredCalls.length === 0 ? (
					<Text className="font-sans text-footnote text-light-text-secondary dark:text-white/55 text-center px-md py-xl">
						{query.trim().length > 0 ? "No matches." : "No call history yet."}
					</Text>
				) : (
					<View className="flex flex-col gap-[2px]">
						{filteredCalls.map((c) => (
							<CallHistoryRow
								key={c.id}
								record={c}
								onStartCall={onStartCall}
								{...(onMorePress !== undefined ? { onMorePress } : {})}
							/>
						))}
					</View>
				)}
			</View>
		</ScrollView>
	);
};

interface FavouriteCardProps {
	readonly user: CallParticipant;
	readonly onPress: () => void;
}

function FavouriteCard({ user, onPress }: FavouriteCardProps): JSX.Element {
	return (
		<View
			className="
				flex flex-row items-center gap-sm
				w-[260px] py-sm px-md rounded-xl
				bg-light-surface-secondary dark:bg-white/[0.04]
				border border-light-border-secondary dark:border-white/[0.04]
			"
		>
			<Avatar
				size="medium"
				initials={user.displayName}
				{...(user.avatarUri !== undefined ? { imageUri: user.avatarUri } : {})}
				accessibilityLabel={`Avatar for ${user.displayName}`}
			/>
			<View className="flex-1 min-w-0 flex flex-col">
				<Text
					numberOfLines={1}
					className="font-sans text-footnote font-semibold text-light-text-primary dark:text-white"
				>
					{user.displayName}
				</Text>
				<Text
					numberOfLines={1}
					className="font-sans text-caption text-light-text-secondary dark:text-white/55"
				>
					{user.role ?? user.company ?? ""}
				</Text>
			</View>
			<Pressable
				accessibilityRole="button"
				accessibilityLabel={`Call ${user.displayName}`}
				onPress={onPress}
				className="
					w-9 h-9 rounded-lg items-center justify-center
					bg-light-accent-sky active:opacity-90
				"
			>
				<Phone size={16} strokeWidth={2} color="#ffffff" />
			</Pressable>
		</View>
	);
}

// Overlapping avatar stack for a group call — up to 3 faces + a "+N" chip.
function AvatarStack({
	participants,
}: {
	participants: ReadonlyArray<{ readonly displayName: string; readonly avatarUri?: string }>;
}): JSX.Element {
	const shown = participants.slice(0, 3);
	const extra = participants.length - shown.length;
	return (
		<View className="flex flex-row items-center">
			{shown.map((p, i) => (
				<View
					// biome-ignore lint/suspicious/noArrayIndexKey: roster order is stable per row
					key={`${p.displayName}:${i}`}
					className={i === 0 ? "" : "-ml-2"}
				>
					<Avatar
						size="small"
						initials={p.displayName}
						{...(p.avatarUri !== undefined ? { imageUri: p.avatarUri } : {})}
						accessibilityLabel={`Avatar for ${p.displayName}`}
					/>
				</View>
			))}
			{extra > 0 && (
				<View className="-ml-2 w-8 h-8 rounded-full items-center justify-center border border-white dark:border-[#0A0B0F] bg-light-surface-tertiary dark:bg-white/[0.08]">
					<Text className="font-sans text-[10px] font-bold text-light-text-secondary dark:text-white/70">
						{`+${extra}`}
					</Text>
				</View>
			)}
		</View>
	);
}

interface CallHistoryRowProps {
	readonly record: CallHistoryEntry;
	readonly onStartCall: (userId: string, kind: CallKind) => void;
	readonly onMorePress?: ((entry: CallHistoryEntry) => void) | undefined;
}

function CallHistoryRow({
	record,
	onStartCall,
	onMorePress,
}: CallHistoryRowProps): JSX.Element {
	const { user, direction, kind, timestamp, durationSec, participants } = record;
	const directionLabel =
		direction === "missed"
			? "Missed"
			: direction === "incoming"
				? "Incoming"
				: "Outgoing";

	const directionColor = direction === "missed" ? "#F87171" : "#9aa0a6";

	// Group call → overlapping avatar stack (up to 3) + "+N"; otherwise the
	// single peer avatar.
	const isGroup = participants !== undefined && participants.length > 1;
	const groupTitle = isGroup
		? participants.map((p) => p.displayName).join(", ")
		: user.displayName;

	return (
		<View
			className="
				flex flex-row items-center gap-md
				px-md py-sm rounded-lg
			"
		>
			{isGroup ? (
				<AvatarStack participants={participants} />
			) : (
				<Avatar
					size="medium"
					initials={user.displayName}
					{...(user.avatarUri !== undefined ? { imageUri: user.avatarUri } : {})}
					accessibilityLabel={`Avatar for ${user.displayName}`}
				/>
			)}
			<View className="flex-1 min-w-0 flex flex-col">
				<Text
					numberOfLines={1}
					className={`
						font-sans text-footnote font-semibold
						${
							direction === "missed"
								? "text-light-accent-rose"
								: "text-light-text-primary dark:text-white"
						}
					`}
				>
					{groupTitle}
				</Text>
				<View className="flex flex-row items-center gap-xs">
					{direction === "missed" ? (
						<PhoneMissed size={12} strokeWidth={2} color={directionColor} />
					) : direction === "incoming" ? (
						<ArrowDownLeft size={12} strokeWidth={2} color={directionColor} />
					) : (
						<ArrowUpRight size={12} strokeWidth={2} color={directionColor} />
					)}
					<Text
						numberOfLines={1}
						className="font-sans text-caption text-light-text-secondary dark:text-white/55"
					>
						{directionLabel}
						{durationSec !== undefined && ` · ${formatDuration(durationSec)}`}
					</Text>
				</View>
			</View>
			<Text className="font-sans text-caption text-light-text-tertiary dark:text-white/55">
				{formatTimestamp(timestamp)}
			</Text>
			<Pressable
				accessibilityRole="button"
				accessibilityLabel={`${
					kind === "video" ? "Video call" : "Call"
				} ${user.displayName}`}
				onPress={() => onStartCall(user.id, kind)}
				className="
					w-9 h-9 rounded-lg items-center justify-center
					active:opacity-70
				"
			>
				{kind === "video" ? (
					<Video size={16} strokeWidth={1.75} color="#9aa0a6" />
				) : (
					<Phone size={16} strokeWidth={1.75} color="#9aa0a6" />
				)}
			</Pressable>
			{onMorePress !== undefined && (
				<Pressable
					accessibilityRole="button"
					accessibilityLabel="More"
					onPress={() => onMorePress(record)}
					className="
						w-9 h-9 rounded-lg items-center justify-center
						active:opacity-70
					"
				>
					<MoreHorizontal size={16} strokeWidth={1.75} color="#9aa0a6" />
				</Pressable>
			)}
		</View>
	);
}

function CallHistorySkeleton(): JSX.Element {
	return (
		<View className="flex flex-col gap-[2px]">
			{Array.from({ length: 6 }).map((_, i) => (
				<View
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
					key={i}
					className="flex flex-row items-center gap-md py-sm px-xs"
				>
					<View className="w-10 h-10 rounded-full bg-light-surface-tertiary dark:bg-white/[0.06]" />
					<View className="flex-1 flex flex-col gap-[6px]">
						<View className="h-[13px] rounded bg-light-surface-tertiary dark:bg-white/[0.06]" style={{ width: "40%" }} />
						<View className="h-[11px] rounded bg-light-surface-tertiary dark:bg-white/[0.06]" style={{ width: "25%" }} />
					</View>
					<View className="flex flex-col items-end gap-[6px]">
						<View className="h-[11px] w-8 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
						<View className="w-9 h-9 rounded-lg bg-light-surface-tertiary dark:bg-white/[0.06]" />
					</View>
				</View>
			))}
		</View>
	);
}

function formatDuration(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatTimestamp(timestamp: number): string {
	const date = new Date(timestamp);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const hours = diffMs / HOUR;
	if (hours < 1) {
		const mins = Math.max(1, Math.floor(diffMs / MIN));
		return `${mins}m`;
	}
	if (hours < 24) {
		return date.toLocaleTimeString(undefined, {
			hour: "numeric",
			minute: "2-digit",
		});
	}
	if (hours < 24 * 7) {
		return date.toLocaleDateString(undefined, { weekday: "short" });
	}
	return date.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
	});
}
